import { requestGQL } from 'common/graphQlClient';
import { useMutation, useQueryClient } from 'react-query';

import { propertyByIdKey } from 'app/properties/hooks/useProperty';
import { clearPersistedQueryClientCache } from 'common/offline/reactQueryPersistence';
import { workSessionByPropertyIdKey } from './useActiveWorkSession';
import { START_WORK_SESSION, StartWorkSessionResponse, STOP_WORK_SESSION, StopWorkSessionResponse } from '../api';
import { usePrefetchWorkSessionData } from './usePrefetchWorkSessionData';

type Options = {
  onStarted?: () => void;
  onStartError?: (err: unknown) => void;
  onEnded?: () => void;
  onEndError?: (err: unknown) => void;
};

export const useWorkSessionActions = (propertyId: number | null, opts: Options = {}) => {
  const qc = useQueryClient();

  const { prefetch } = usePrefetchWorkSessionData(propertyId);

  const startMutation = useMutation(
    async () => {
      if (propertyId == null) throw new Error('Missing propertyId');

      return requestGQL<StartWorkSessionResponse>(START_WORK_SESSION, {
        propertyId,
      });
    },
    {
      onSuccess: async () => {
        await qc.invalidateQueries(workSessionByPropertyIdKey(propertyId));
        await qc.invalidateQueries(propertyByIdKey(propertyId));

        // Refetch in the background so fresh data is in cache before navigation
        qc.refetchQueries('allProperties');

        await prefetch();
        opts.onStarted?.();
      },
      onError: (err) => {
        opts.onStartError?.(err);
      },
    },
  );

  const endMutation = useMutation(
    async (workSessionId: number) => {
      return requestGQL<StopWorkSessionResponse>(STOP_WORK_SESSION, {
        workSessionId,
      });
    },
    {
      onSuccess: async () => {
        await qc.invalidateQueries(workSessionByPropertyIdKey(propertyId));
        await qc.invalidateQueries(propertyByIdKey(propertyId));

        // Refetch in the background so fresh data is in cache before navigation
        qc.refetchQueries('allProperties');

        await qc.refetchQueries(workSessionByPropertyIdKey(propertyId), { exact: true });
        opts.onEnded?.();
        await clearPersistedQueryClientCache();
      },
      onError: (err) => {
        opts.onEndError?.(err);
      },
    },
  );

  return {
    startWorkSession: startMutation.mutateAsync,
    endWorkSession: endMutation.mutateAsync,
    isStarting: startMutation.isLoading,
    isEnding: endMutation.isLoading,
  };
};
